import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { CreateQuranTargetPayload, QuranTargetRow, createQuranTrackerApi, QuranTrackerScope } from '@/services/quran-tracker.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';

const columnHelper = createColumnHelper<QuranTargetRow>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** QT Target — class+surah target definitions (Quran Tracker). `scope="organization"` renders this natively inside the Org Console, hitting the org's own operating school — no "enter a school" step, matching the real legacy app. */
export function QuranTargetsPage({ scope = 'school' }: { scope?: QuranTrackerScope }) {
  const term = useTerminology();
  const qc = useQueryClient();
  const quranApi = useMemo(() => createQuranTrackerApi(scope), [scope]);
  const dt = useDataTable({ defaultLimit: 20 });
  const [classFilter, setClassFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classFilter]);

  const isOrg = scope === 'organization';
  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', scope, 'for-quran-targets'],
    queryFn: () => (isOrg ? quranApi.classesPicker() : AcademicClassesApi.list({ limit: 100 }).then((r) => r.items)),
  });
  const classes = classesPage ?? [];

  const { data, isFetching } = useQuery({
    queryKey: ['quran-targets', scope, { ...dt.params, classFilter }],
    queryFn: () => quranApi.listTargets({ ...dt.params, classId: classFilter || undefined }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['quran-targets', scope] });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('className', { header: term.level }),
    columnHelper.accessor('surahName', { header: 'Surah' }),
    columnHelper.display({
      id: 'ayahRange',
      header: 'Ayah Range',
      cell: (ctx) => `${ctx.row.original.ayathFrom} – ${ctx.row.original.ayathTo}`,
    }),
  ];

  return (
    <>
      <PageHeader
        title="QT Target"
        description="Quran Tracker target definitions — a surah + ayah range assigned to a class."
        actions={
          isOrg && (
            <button className="btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> New Target
            </button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-48" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
          <option value="">All {term.levelPlural}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable columns={columns} result={data} state={dt} loading={isFetching} emptyMessage="No targets defined yet." />

      {isOrg && (
        <CreateTargetModal open={createOpen} onClose={() => setCreateOpen(false)} onSaved={invalidate} classes={classes} quranApi={quranApi} />
      )}
    </>
  );
}

function CreateTargetModal({
  open,
  onClose,
  onSaved,
  classes,
  quranApi,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  classes: { id: string; name: string }[];
  quranApi: ReturnType<typeof createQuranTrackerApi>;
}) {
  const [classId, setClassId] = useState('');
  const [surahId, setSurahId] = useState('');
  const [ayathFrom, setAyathFrom] = useState('');
  const [ayathTo, setAyathTo] = useState('');

  useEffect(() => {
    if (open) {
      setClassId('');
      setSurahId('');
      setAyathFrom('');
      setAyathTo('');
    }
  }, [open]);

  const { data: surahs = [] } = useQuery({
    queryKey: ['quran-surahs-picker'],
    queryFn: quranApi.surahsPicker,
    enabled: open,
  });

  const save = useMutation({
    mutationFn: (payload: CreateQuranTargetPayload) => quranApi.createTarget(payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const valid = !!classId && !!surahId && !!ayathFrom && !!ayathTo && Number(ayathFrom) <= Number(ayathTo);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Quran Tracker Target"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={save.isPending || !valid}
            onClick={() =>
              save.mutate({ classId, surahId, ayathFrom: Number(ayathFrom), ayathTo: Number(ayathTo) })
            }
          >
            {save.isPending ? 'Saving…' : 'Create Target'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Class" required>
          <Select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">— Select class —</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Surah" required>
          <Select value={surahId} onChange={(e) => setSurahId(e.target.value)}>
            <option value="">— Select surah —</option>
            {surahs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nameEng} ({s.ayathCount} ayahs)
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ayah From" required>
            <Input type="number" min="1" value={ayathFrom} onChange={(e) => setAyathFrom(e.target.value)} />
          </Field>
          <Field label="Ayah To" required>
            <Input type="number" min="1" value={ayathTo} onChange={(e) => setAyathTo(e.target.value)} />
          </Field>
        </div>
        {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}
      </div>
    </Modal>
  );
}
